import axios, { AxiosError } from "axios";
import { base64UrlDecode } from "@/utils/base64-url";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from "@/utils/token-cookie";
import type { ErrorResponse } from "@/utils/uno-axios";

/**
 * access token 갱신의 단일 창구.
 *
 * 예전에는 uno-axios와 axios-client가 각자 갱신 로직을 들고 있었다. 같은 쿠키 한 쌍을
 * 두고 두 개의 단일비행(single-flight) 가드가 따로 도는 구조라, 백그라운드에서 복귀해
 * 두 스택이 동시에 401을 맞으면 /auth/refresh가 두 번 나갔다. 서버가 refresh token을
 * 회전시키면 진 쪽이 4xx를 받고, 그 응답으로 쿠키를 덮어써 멀쩡한 토큰을 날렸다.
 *
 * 의존성은 axios / base64-url / token-cookie로만 제한한다. jwt.ts는 @/features/types를
 * 거쳐 무거운 그래프를 끌고 오는데, 소비 모듈이 40여 개인 axios-client가 그걸 새로
 * 들이지 않게 하기 위함이다. 그래서 만료 판별도 여기서 직접 디코딩한다.
 */

const SIGN_IN_PATH = "/auth/sign-in";
const REFRESH_TIMEOUT_MS = 30 * 1000;

/**
 * 전송 실패로 갱신이 깨진 뒤 다시 시도하기까지의 최소 간격.
 *
 * 이게 없으면 화면의 모든 쿼리가 각자 갱신을 시도하고, 실패한 요청을 react-query가
 * 또 재시도하면서 /auth/refresh를 초당 수십 번 때린다(실제로 359요청 중 86건이
 * 그 폭풍이었다). 서버가 5xx를 주는 동안 조용히 기다렸다가 다시 붙는다.
 */
const TRANSIENT_RETRY_COOLDOWN_MS = 5 * 1000;

/**
 * 만료를 기다리지 않고 갱신 경로를 밟아보기 위한 개발/QA용 스위치.
 * token-cookie.ts의 __force_persistent_login과 같은 방식이다.
 *
 * access token 수명이 999분이라 "화면 오래 켜두기"를 실제로 재현할 수 없고,
 * 쿠키 값을 손으로 훼손하면 만료가 아니라 서명 위조가 돼서 서버가 다른 경로를 탄다.
 * 그래서 프론트 쪽 판정만 한 번 뒤집어 준다.
 *
 *   localStorage.setItem("__force_token_expired", "1")
 *
 * 한 번 쓰이면 스스로 지워진다. 안 그러면 모든 요청이 매번 갱신을 끌고 다닌다.
 */
const FORCE_EXPIRED_KEY = "__force_token_expired";

function consumeForcedExpiryByDeveloper() {
  if (process.env.NODE_ENV === "production" || typeof window === "undefined") {
    return false;
  }

  try {
    if (localStorage.getItem(FORCE_EXPIRED_KEY) !== "1") {
      return false;
    }
    localStorage.removeItem(FORCE_EXPIRED_KEY);
    return true;
  } catch (e) {
    // 사파리 프라이빗 모드 등에서 localStorage 접근 자체가 throw할 수 있다.
    return false;
  }
}

/**
 * 선제 갱신을 건너뛸 경로.
 *
 * /auth/refresh 자신은 물론이고 로그인·회원가입·비밀번호 찾기까지 전부 제외한다.
 * 만료된 쿠키가 남은 채로 비밀번호 찾기 화면에 들어온 사용자를, 갱신 실패를 이유로
 * 로그인 화면으로 튕겨내면 안 된다.
 */
export function isAuthUrl(url: string | undefined) {
  return !!url && url.startsWith("/auth");
}

/**
 * 갱신 실패의 두 종류를 구분한다.
 *
 * terminal - 서버가 세션을 거절했다(4xx). 또는 200인데 응답에 토큰이 없다.
 *            더 해볼 게 없으므로 로그인 화면으로 보낸다.
 * transient - 응답 자체가 없거나(오프라인·타임아웃) 5xx. 전송이 실패한 것뿐이라
 *            멀쩡한 토큰을 지우면 안 된다. 지하철에서 앱을 열었다고 로그아웃되면 안 된다.
 */
export class RefreshFailure extends Error {
  readonly terminal: boolean;
  readonly reason?: unknown;

  constructor(message: string, terminal: boolean, reason?: unknown) {
    super(message);
    this.name = "RefreshFailure";
    this.terminal = terminal;
    this.reason = reason;
  }
}

export function isTerminalRefreshFailure(error: unknown): boolean {
  return error instanceof RefreshFailure && error.terminal;
}

export function createRefreshFailedErrorResponse(): ErrorResponse {
  return {
    error: {
      code: 401,
      status: "JWT_TOKEN_REFRESH_FAILED",
      message:
        "Oops! We're having trouble keeping you signed in right now. Please Sign in again",
    },
  };
}

/**
 * 세션이 끝난 걸로 봐야 하는 에러인지.
 *
 * 인터셉터가 갱신·재시도를 이미 한 번 끝낸 뒤에만 401이 호출부까지 올라온다.
 * 그러니 여기까지 온 401은 "더 해볼 게 없는" 상태다. 403은 제외한다 —
 * 인증은 됐는데 권한이 없는 경우까지 로그아웃시키면 안 된다.
 */
export function isSessionEndedError(error: unknown): boolean {
  const candidate = error as any;
  const code =
    candidate?.error?.code ?? candidate?.response?.status ?? candidate?.code;
  const status =
    candidate?.error?.status ?? candidate?.response?.data?.error?.status;

  return (
    code === 401 ||
    status === "UNAUTHENTICATED" ||
    status === "JWT_TOKEN_REFRESH_FAILED" ||
    status === "JWT_TOKEN_EXPIRED" ||
    status === "JWT_TOKEN_INVALIDATED"
  );
}

let isEndingSession = false;

/**
 * 세션을 끝내고 로그인 화면으로 보낸다. 여러 요청이 동시에 실패해도 한 번만 이동한다.
 * SPA navigate가 아니라 하드 이동인 이유는, 죽은 세션으로 남은 메모리 상태
 * (recoil / react-query 캐시 / SWR 캐시)를 전부 버려야 하기 때문이다.
 */
export function endSession() {
  if (typeof window === "undefined" || isEndingSession) {
    return;
  }

  const hadSession = !!getAccessToken() || !!getRefreshToken();
  clearTokens();

  // 애초에 로그인 상태가 아니었으면 끝낼 세션도 없다. 공개 페이지에서 401이 한 번
  // 났다고 방문자를 로그인 화면으로 끌고 가면 안 된다.
  if (!hadSession || window.location.pathname === SIGN_IN_PATH) {
    return;
  }

  isEndingSession = true;
  const redirectUrl = encodeURIComponent(
    window.location.pathname + window.location.search
  );
  window.location.href = `${SIGN_IN_PATH}?redirect_url=${redirectUrl}`;
}

/**
 * 갱신 실패를 호출부가 던질 값으로 바꾼다.
 * terminal이면 세션을 끝내고 앱이 이미 이해하는 인증 에러 모양으로,
 * transient면 원인 그대로(네트워크 에러)를 돌려준다 — 만료된 401을 그대로 흘리면
 * 사용자에게는 "왜인지 모를 401"로만 보인다.
 */
export function toRefreshFailureError(error: unknown): unknown {
  if (isTerminalRefreshFailure(error)) {
    endSession();
    return createRefreshFailedErrorResponse();
  }

  if (error instanceof RefreshFailure && error.reason !== undefined) {
    return error.reason;
  }

  return error;
}

/** 디코딩 가능한 JWT면 payload를, 아니면 undefined를 돌려준다. */
function readJwtPayload(token: string | undefined): any | undefined {
  if (!token) {
    return undefined;
  }
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return undefined;
    }
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch (e) {
    return undefined;
  }
}

/**
 * 지금 들고 있는 access token으로는 요청을 보내면 안 되는 상태인지.
 *
 * - 만료됐거나 곧 만료된다
 * - 디코딩 자체가 안 된다(훼손). 그대로 보내면 서버가 만료와 다른 status로 401을 준다
 * - access token은 없는데 refresh token은 있다(쿠키가 부분적으로 날아간 상태)
 *
 * 둘 다 없으면 false다 — 갱신할 근거가 없고, 로그인 안 한 방문자다.
 */
export function isAccessTokenExpired(bufferInMilliseconds: number = 15 * 1000) {
  if (consumeForcedExpiryByDeveloper()) {
    return true;
  }

  const accessToken = getAccessToken();
  if (!accessToken) {
    return !!getRefreshToken();
  }

  const payload = readJwtPayload(accessToken);
  const exp = payload ? parseInt(payload.exp, 10) : NaN;
  if (!exp) {
    return true;
  }

  return exp * 1000 < Date.now() + bufferInMilliseconds;
}

let inFlight: Promise<string> | null = null;
let lastTransientFailure: { at: number; error: RefreshFailure } | null = null;

/**
 * 새 access token을 받아 쿠키에 기록하고 돌려준다.
 * 동시에 여러 번 불려도 실제 요청은 한 번만 나간다.
 */
export function refreshAccessToken(): Promise<string> {
  if (inFlight) {
    return inFlight;
  }

  // 방금 전송 실패로 깨졌으면 잠깐 쉰다. 같은 실패를 그대로 돌려주므로 호출부의
  // 분기는 달라지지 않고, 네트워크만 안 때린다.
  if (
    lastTransientFailure &&
    Date.now() - lastTransientFailure.at < TRANSIENT_RETRY_COOLDOWN_MS
  ) {
    return Promise.reject(lastTransientFailure.error);
  }

  inFlight = requestNewTokens()
    .then((token) => {
      lastTransientFailure = null;
      return token;
    })
    .catch((e) => {
      if (e instanceof RefreshFailure && !e.terminal) {
        lastTransientFailure = { at: Date.now(), error: e };
      }
      throw e;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

async function postRefresh(
  refreshToken: string,
  accessToken: string | undefined
) {
  // 인터셉터가 붙은 인스턴스를 쓰지 않는다. 갱신 응답이 다시 응답 인터셉터를 타면
  // 재귀하고, 요청 인터셉터의 snake/camel 변환에도 끌려다닌다.
  const response = await axios.post(
    `${process.env.NEXT_PUBLIC_API_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    {
      timeout: REFRESH_TIMEOUT_MS,
      headers: {
        "ngrok-skip-browser-warning": "1",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    }
  );
  return response.data;
}

function toRefreshFailure(error: unknown) {
  const status = (error as AxiosError)?.response?.status;
  const isRejectedByServer =
    status !== undefined && status >= 400 && status < 500;
  return new RefreshFailure("/auth/refresh 실패", isRejectedByServer, error);
}

async function requestNewTokens(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new RefreshFailure("refresh token이 없다", true);
  }

  // 디코딩도 안 되는 토큰은 애초에 싣지 않는다.
  const accessToken = getAccessToken();
  const bearer = readJwtPayload(accessToken) ? accessToken : undefined;

  let data: any;
  try {
    data = await postRefresh(refreshToken, bearer);
  } catch (e) {
    const failure = toRefreshFailure(e);

    // 4xx는 서버가 세션을 판단한 결과이므로 그대로 받는다.
    // 그 외(5xx·응답 없음)인데 Authorization을 실어 보냈다면 그 헤더가 원인일 수 있다.
    // 훼손된 access token을 백엔드가 파싱하다 500을 내면, 500 응답에는 CORS 헤더가
    // 없어 브라우저가 통째로 막아버린다. 프론트에는 "응답 없음"으로만 보여서
    // 전송 실패로 분류되고, 세션이 죽었는데도 로그아웃되지 않는다.
    // 갱신의 자격증명은 바디의 refresh token이므로 헤더를 빼고 딱 한 번 다시 붙어본다.
    if (failure.terminal || !bearer) {
      throw failure;
    }

    try {
      data = await postRefresh(refreshToken, undefined);
    } catch (retryError) {
      throw toRefreshFailure(retryError);
    }
  }

  // 서버 표기가 snake인지 camel인지에 코드가 걸려 있으면 안 된다. 예전에 axios-client는
  // snake로만, uno-axios는 camel로만 읽었고 둘 다 맞을 수는 없었다.
  const nextAccessToken = data?.access_token ?? data?.accessToken;
  const nextRefreshToken = data?.refresh_token ?? data?.refreshToken;

  // 200인데 토큰이 없으면 성공이 아니다. 여기서 막지 않으면 만료된 토큰을 그대로 들고
  // 재시도해 401을 무한히 맞는다 — 이게 "갱신했는데도 401이 뜨는" 상태의 정체다.
  if (!nextAccessToken) {
    throw new RefreshFailure("/auth/refresh 응답에 access token이 없다", true);
  }

  setTokens(nextAccessToken, nextRefreshToken);
  return nextAccessToken;
}
